import { useState, useEffect } from 'react';
import { FiDollarSign, FiClock, FiUser, FiRefreshCw, FiAlertTriangle, FiCheckCircle } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import exchangeRateService from '../services/exchangeRateService';
import toast from 'react-hot-toast';

const ExchangeRatePage = () => {
  const { admin, isSuperAdmin } = useAdminAuth();
  const navigate = useNavigate();
  
  const [currentRate, setCurrentRate] = useState(null);
  const [rateHistory, setRateHistory] = useState([]);
  const [isRateSet, setIsRateSet] = useState(false);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [newRate, setNewRate] = useState('');
  const [previousRate, setPreviousRate] = useState(null);
  const [error, setError] = useState(null);

  // Check if user is SuperAdmin
  useEffect(() => {
    if (!isSuperAdmin()) {
      toast.error('Access denied. SuperAdmin privileges required.');
      navigate('/dashboard');
      return;
    }
  }, [isSuperAdmin, navigate]);

  // Load exchange rate data
  useEffect(() => {
    loadExchangeRateData();
  }, []);

  const loadExchangeRateData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Load current rate and status
      const [rateData, statusData, historyData] = await Promise.all([
        exchangeRateService.getCurrentRate(),
        exchangeRateService.isRateSet(),
        exchangeRateService.getRateHistory()
      ]);
      
      const formattedRate = exchangeRateService.formatRateForDisplay(rateData);
      const formattedHistory = exchangeRateService.formatRateHistory(historyData);
      
      setCurrentRate(formattedRate);
      setIsRateSet(statusData.isSet);
      setRateHistory(formattedHistory);
      
      // Set previous rate for comparison (second item in history if available)
      if (formattedHistory.length > 0) {
        setPreviousRate(formattedHistory[0].rate);
      }
      
      // Set new rate input to current rate
      if (formattedRate) {
        setNewRate(formattedRate.rate.toString());
      }
    } catch (err) {
      console.error('Error loading exchange rate data:', err);
      setError(err.message || 'Failed to load exchange rate data');
      toast.error('Failed to load exchange rate data');
    } finally {
      setLoading(false);
    }
  };

  const handleRateUpdate = async (e) => {
    e.preventDefault();
    
    if (!isSuperAdmin()) {
      toast.error('Access denied. SuperAdmin privileges required.');
      return;
    }
    
    try {
      setUpdating(true);
      setError(null);
      
      // Validate input
      const validatedRate = exchangeRateService.validateRateInput(newRate);
      
      // Update rate
      const result = await exchangeRateService.updateRate(validatedRate);
      const formattedResult = exchangeRateService.formatRateForDisplay(result);
      
      setCurrentRate(formattedResult);
      setIsRateSet(true);
      
      // Add to history (simulate adding new record)
      const newHistoryRecord = {
        id: Date.now(),
        rate: validatedRate,
        formattedRate: `1 Pi = ₦${validatedRate.toLocaleString()}`,
        fromCurrency: 'PI',
        toCurrency: 'NGN',
        source: 'admin',
        createdAt: new Date().toLocaleString(),
        updatedBy: admin?.username || 'Unknown',
        description: `Updated by ${admin?.username || 'Unknown'} at ${new Date().toLocaleString()}`
      };
      
      setRateHistory([newHistoryRecord, ...rateHistory]);
      setPreviousRate(currentRate?.rate);
      
      toast.success('Exchange rate updated successfully');
      
      // Reset form
      setNewRate(validatedRate.toString());
      
    } catch (err) {
      console.error('Error updating exchange rate:', err);
      setError(err.message || 'Failed to update exchange rate');
      toast.error(err.message || 'Failed to update exchange rate');
    } finally {
      setUpdating(false);
    }
  };

  const handleRefresh = () => {
    loadExchangeRateData();
  };

  const rateChange = currentRate && previousRate 
    ? exchangeRateService.getRateChangePercentage(currentRate.rate, previousRate)
    : null;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-teal"></div>
        <span className="ml-3 text-gray-600">Loading exchange rate data...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Exchange Rate Management</h1>
          <p className="text-gray-600 mt-1">
            Manage the Pi to NGN exchange rate for the platform
          </p>
        </div>
        <Button
          variant="outline"
          onClick={handleRefresh}
          disabled={loading || updating}
          className="flex items-center"
        >
          <FiRefreshCw className={`mr-2 h-4 w-4 ${loading || updating ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Alert for SuperAdmin requirement */}
      {!isSuperAdmin() && (
        <Card className="border-yellow-200 bg-yellow-50">
          <CardContent className="p-4">
            <div className="flex items-center">
              <FiAlertTriangle className="h-5 w-5 text-yellow-600 mr-3" />
              <div>
                <p className="text-sm font-medium text-yellow-800">
                  SuperAdmin Privileges Required
                </p>
                <p className="text-sm text-yellow-700">
                  Only SuperAdmins can modify exchange rates.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Error Display */}
      {error && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-4">
            <div className="flex items-center">
              <FiAlertTriangle className="h-5 w-5 text-red-600 mr-3" />
              <p className="text-sm text-red-800">{error}</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Current Rate Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <FiDollarSign className="h-5 w-5 text-primary-teal mr-2" />
            Current Exchange Rate
          </CardTitle>
          <CardDescription>
            The current rate used for all Pi to NGN conversions
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isRateSet ? (
            <div className="flex items-center justify-between">
              <div>
                <div className="text-3xl font-bold text-gray-900">
                  {currentRate?.formattedRate}
                </div>
                <div className="flex items-center mt-2 text-sm text-gray-600">
                  <FiClock className="h-4 w-4 mr-1" />
                  Last updated: {currentRate?.updatedAt}
                  {currentRate?.updatedBy && (
                    <span className="ml-4 flex items-center">
                      <FiUser className="h-4 w-4 mr-1" />
                      By: {currentRate?.updatedBy}
                    </span>
                  )}
                </div>
                {rateChange && (
                  <div className={`mt-2 text-sm font-medium ${
                    rateChange.direction === 'increase' ? 'text-green-600' : 
                    rateChange.direction === 'decrease' ? 'text-red-600' : 'text-gray-600'
                  }`}>
                    {rateChange.formattedPercentage} from previous rate
                  </div>
                )}
              </div>
              <FiCheckCircle className="h-12 w-12 text-green-500" />
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold text-gray-500">
                  No Rate Set
                </div>
                <p className="text-sm text-gray-600 mt-2">
                  Please set an exchange rate to enable Pi to NGN conversions.
                </p>
              </div>
              <FiAlertTriangle className="h-12 w-12 text-yellow-500" />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Rate Update Form */}
      {isSuperAdmin() && (
        <Card>
          <CardHeader>
            <CardTitle>Update Exchange Rate</CardTitle>
            <CardDescription>
              Set a new exchange rate for Pi to NGN conversions
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleRateUpdate} className="space-y-4">
              <div>
                <label htmlFor="rate" className="block text-sm font-medium text-gray-700 mb-2">
                  New Exchange Rate
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <span className="text-gray-500 sm:text-sm">1 Pi = ₦</span>
                  </div>
                  <input
                    type="text"
                    id="rate"
                    name="rate"
                    value={newRate}
                    onChange={(e) => {
                      // Allow only numbers and one decimal point
                      const value = e.target.value;
                      if (/^\d*\.?\d*$/.test(value) || value === '') {
                        setNewRate(value);
                      }
                    }}
                    step="0.01"
                    min="0.000001"
                    max="1000000"
                    placeholder="1000.00"
                    className="block w-full pl-24 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-primary-teal focus:border-primary-teal"
                    required
                  />
                </div>
                <p className="mt-1 text-sm text-gray-500">
                  Enter the exchange rate (1 Pi = ₦X). Must be between 0.000001 and 1,000,000.
                </p>
              </div>
              
              <div className="flex justify-end space-x-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleRefresh}
                  disabled={loading || updating}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={updating || !newRate}
                  className="flex items-center"
                >
                  {updating ? (
                    <>
                      <FiRefreshCw className="mr-2 h-4 w-4 animate-spin" />
                      Updating...
                    </>
                  ) : (
                    <>
                      <FiCheckCircle className="mr-2 h-4 w-4" />
                      Update Rate
                    </>
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Rate History */}
      {rateHistory.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Rate History</CardTitle>
            <CardDescription>
              Recent exchange rate changes and updates
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {rateHistory.map((record) => (
                <div key={record.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-4">
                    <div className="w-10 h-10 bg-primary-teal/10 rounded-full flex items-center justify-center">
                      <FiDollarSign className="h-5 w-5 text-primary-teal" />
                    </div>
                    <div>
                      <div className="font-medium text-gray-900">
                        {record.formattedRate}
                      </div>
                      <div className="text-sm text-gray-600">
                        {record.description}
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm text-gray-500">
                      {record.createdAt}
                    </div>
                    <div className="text-xs text-gray-400">
                      Source: {record.source}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Empty State */}
      {!isRateSet && rateHistory.length === 0 && (
        <Card>
          <CardContent className="p-12 text-center">
            <FiDollarSign className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No Exchange Rate History
            </h3>
            <p className="text-gray-600">
              Set your first exchange rate to start tracking changes.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ExchangeRatePage;